import React, { useState } from 'react';
import { useOpportunities } from '../../hooks/useOpportunities';
import { useAuth } from '../../hooks/useAuth';
import { clearAllOpportunities } from '../../services/opportunityService';
import { Trash2, Target } from '../../icons';
import OpportunityCard from './OpportunityCard';
import OpportunityFilters from './OpportunityFilters';
import OpportunityStats from './OpportunityStats';
import Button from '../ui/Button';
import Sheet from '../ui/Sheet';
import EmptyState from '../ui/EmptyState';
import ErrorDisplay from '../common/ErrorDisplay';

export default function OpportunityDashboard() {
  const { currentUser, isAuthReady } = useAuth();
  const userId = currentUser?.uid;
  const { opportunities, isLoading, error } = useOpportunities(userId, isAuthReady);
  const [filter, setFilter] = useState('active');
  const [sortBy, setSortBy] = useState('score');
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  
  const filteredOpportunities = opportunities
    .filter(opp => {
      if (filter === 'active') return opp.status === 'active';
      if (filter === 'dismissed') return opp.status === 'dismissed';
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'score') {
        return (b.aiAnalysis?.investmentScore || 0) - (a.aiAnalysis?.investmentScore || 0);
      }
      if (sortBy === 'date') {
        return new Date(b.detectedAt) - new Date(a.detectedAt);
      }
      if (sortBy === 'discount') {
        return (b.bestDiscount || 0) - (a.bestDiscount || 0);
      }
      return 0;
    });
  
  if (!isAuthReady || isLoading) {
    return (
      <div className="space-y-4" aria-busy="true" aria-label="Loading opportunities">
        <div className="grid grid-cols-3 gap-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-20 bg-card border border-line rounded-card animate-pulse" />
          ))}
        </div>
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="bg-card border border-line rounded-card overflow-hidden animate-pulse">
              <div className="aspect-[16/9] bg-card-2" />
              <div className="p-4 space-y-2">
                <div className="h-5 w-32 bg-card-2 rounded" />
                <div className="h-4 w-3/4 bg-card-2 rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  if (error) {
    return <ErrorDisplay title="Error loading opportunities" error={error} />;
  }
  
  return (
    <div className="space-y-4">
      {/* Intro + clear */}
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-secondary">Automatically detected properties below market value.</p>
        {opportunities.length > 0 && (
          <Button variant="ghost" size="sm" onClick={() => setShowClearConfirm(true)} className="text-danger hover:text-danger flex-shrink-0">
            <Trash2 className="w-4 h-4" /> Clear all ({opportunities.length})
          </Button>
        )}
      </div>

      {/* Clear All confirmation */}
      <Sheet isOpen={showClearConfirm} onClose={() => !isClearing && setShowClearConfirm(false)} mode="dialog" size="sm" title="Clear all opportunities">
        <p className="text-sm text-primary">
          Delete all <strong>{opportunities.length}</strong> opportunities?
        </p>
        <p className="text-xs text-secondary mt-2">
          This permanently removes all opportunity data. New opportunities will be detected on the next scanner run.
        </p>
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="secondary" onClick={() => setShowClearConfirm(false)} disabled={isClearing}>Cancel</Button>
          <Button
            variant="danger-solid"
            loading={isClearing}
            onClick={async () => {
              setIsClearing(true);
              try {
                await clearAllOpportunities(userId);
              } catch (err) {
                console.error('Error clearing opportunities:', err);
              } finally {
                setIsClearing(false);
                setShowClearConfirm(false);
              }
            }}
          >
            {!isClearing && <Trash2 className="w-4 h-4" />}
            {isClearing ? 'Clearing' : 'Clear all'}
          </Button>
        </div>
      </Sheet>
        
      {/* Stats */}
      <OpportunityStats opportunities={opportunities} />
        
      {/* Filters */}
      <OpportunityFilters 
        filter={filter}
        setFilter={setFilter}
        sortBy={sortBy}
        setSortBy={setSortBy}
        totalCount={filteredOpportunities.length}
      />
        
      {/* Opportunities Grid */}
      {filteredOpportunities.length === 0 ? (
        <EmptyState
          icon={Target}
          title={filter === 'active' ? 'No active opportunities' : 'No opportunities found'}
          description={
            filter === 'active'
              ? 'New opportunities will appear here when they are detected. Make sure you have tracked searches with opportunity tracking enabled.'
              : 'Try adjusting your filters or check back later.'
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {filteredOpportunities.map(opp => (
            <OpportunityCard 
              key={opp.id} 
              opportunity={opp} 
              userId={userId} 
            />
          ))}
        </div>
      )}
    </div>
  );
}
